/**
 * Jalali (Persian) calendar formatting (ADR-0012 §6).
 *
 * Storage and transport stay ISO-8601 UTC; only the presentation layer converts. The conversion is done by the
 * platform's own ICU data (`Intl.DateTimeFormat` with the `persian` calendar) rather than by a calendar library,
 * which keeps the bundle small and the leap-year rules correct. A date picker with calendar *arithmetic* arrives
 * with the wizard (Phase 6) and may add `date-fns-jalali` for that purpose only.
 */

import { defaultDigitStyle, toLatinDigits, toPersianDigits } from './numbers';
import type { DigitStyle, UiLocale } from './numbers';

/** Calendar base locale: `fa-IR` gives Persian month names, `en-US` gives English ones. */
function calendarLocale(locale: UiLocale): string {
  return `${locale === 'fa' ? 'fa-IR' : 'en-US'}-u-ca-persian`;
}

/** Presentation styles offered by the design system. */
export type JalaliDateStyle = 'numeric' | 'short' | 'medium' | 'long';

/** Options accepted by the formatters in this module. */
export interface JalaliFormatOptions {
  /** UI locale; controls month names and the time format. */
  locale?: UiLocale;
  /** Digit family; defaults to Persian for `fa` and Latin for `en`. */
  digits?: DigitStyle;
  /** IANA time zone used for the conversion. Defaults to the browser's zone. */
  timeZone?: string;
}

/** Normalizes an ISO string, epoch milliseconds or `Date` into a valid `Date`. */
export function toInstant(value: string | number | Date): Date | null {
  const date = value instanceof Date ? value : new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
}

/** Builds the `Intl` option set for a date style. */
function styleOptions(style: JalaliDateStyle): Intl.DateTimeFormatOptions {
  switch (style) {
    case 'numeric':
      return { year: 'numeric', month: '2-digit', day: '2-digit' };
    case 'short':
      return { dateStyle: 'short' };
    case 'medium':
      return { dateStyle: 'medium' };
    case 'long':
      return { dateStyle: 'full' };
  }
}

/** Removes the era suffix an English Jalali rendering appends (`7/7/1405 AP` → `7/7/1405`). */
function stripEra(value: string): string {
  return value.replace(/\s*(AP|A\.P\.)$/u, '').trim();
}

/** Applies a digit family to an already formatted fragment. */
function applyDigits(value: string, digits: DigitStyle): string {
  return digits === 'persian' ? toPersianDigits(toLatinDigits(value)) : toLatinDigits(value);
}

/**
 * Formats a date in the Jalali calendar.
 *
 * @param value Instant as an ISO string, epoch milliseconds or `Date`.
 * @param options Locale, digit family, time zone and presentation style.
 * @returns The formatted date, or an empty string when the input is not a valid instant.
 */
export function formatJalaliDate(
  value: string | number | Date | null | undefined,
  options: JalaliFormatOptions & { style?: JalaliDateStyle } = {},
): string {
  if (value === null || value === undefined) {
    return '';
  }

  const date = toInstant(value);

  if (!date) {
    return '';
  }

  const locale = options.locale ?? 'fa';
  const digits = options.digits ?? defaultDigitStyle(locale);
  const formatted = new Intl.DateTimeFormat(calendarLocale(locale), {
    ...styleOptions(options.style ?? 'short'),
    timeZone: options.timeZone,
  }).format(date);

  const withoutEra = locale === 'en' ? stripEra(formatted) : formatted;

  // Persian output already carries Persian digits; this makes the requested family authoritative either way.
  return applyDigits(withoutEra, digits);
}

/**
 * Formats a date and a clock time in the Jalali calendar.
 *
 * @param value Instant as an ISO string, epoch milliseconds or `Date`.
 * @param options Locale, digit family, time zone and presentation style.
 * @returns The formatted date-time, or an empty string for an invalid instant.
 */
export function formatJalaliDateTime(
  value: string | number | Date | null | undefined,
  options: JalaliFormatOptions & { style?: JalaliDateStyle } = {},
): string {
  if (value === null || value === undefined) {
    return '';
  }

  const date = toInstant(value);

  if (!date) {
    return '';
  }

  const locale = options.locale ?? 'fa';
  const digits = options.digits ?? defaultDigitStyle(locale);
  const formattedTime = new Intl.DateTimeFormat(locale === 'fa' ? 'fa-IR' : 'en-US', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: options.timeZone,
  }).format(date);

  return `${formatJalaliDate(date, options)} — ${applyDigits(formattedTime, digits)}`;
}

/**
 * Picks the largest unit that still reads naturally, mirroring the thresholds of `Intl.RelativeTimeFormat`.
 */
function largestRelativeUnit(milliseconds: number): {
  value: number;
  unit: Intl.RelativeTimeFormatUnit;
} {
  const seconds = milliseconds / 1000;

  if (Math.abs(seconds) < 45) {
    return { value: Math.round(seconds), unit: 'second' };
  }

  const minutes = seconds / 60;

  if (Math.abs(minutes) < 45) {
    return { value: Math.round(minutes), unit: 'minute' };
  }

  const hours = minutes / 60;

  if (Math.abs(hours) < 22) {
    return { value: Math.round(hours), unit: 'hour' };
  }

  const days = hours / 24;

  if (Math.abs(days) < 26) {
    return { value: Math.round(days), unit: 'day' };
  }

  const months = days / 30;

  if (Math.abs(months) < 11) {
    return { value: Math.round(months), unit: 'month' };
  }

  return { value: Math.round(days / 365), unit: 'year' };
}

/**
 * Formats "how long ago" (or "in how long") in Persian/English.
 *
 * @param value Instant to describe.
 * @param options Locale, digit family and the reference instant (injectable for deterministic tests).
 * @returns A sentence such as `۳ روز پیش`.
 */
export function formatJalaliRelative(
  value: string | number | Date | null | undefined,
  options: JalaliFormatOptions & { now?: Date } = {},
): string {
  if (value === null || value === undefined) {
    return '';
  }

  const date = toInstant(value);

  if (!date) {
    return '';
  }

  const locale = options.locale ?? 'fa';
  const digits = options.digits ?? defaultDigitStyle(locale);
  const reference = options.now ?? new Date();
  const { value: amount, unit } = largestRelativeUnit(date.getTime() - reference.getTime());
  const formatted = new Intl.RelativeTimeFormat(locale === 'fa' ? 'fa' : 'en', {
    numeric: 'auto',
  }).format(amount, unit);

  return applyDigits(formatted, digits);
}

/** Serializes an instant for the `datetime` attribute of a `<time>` element (always ISO-8601 UTC). */
export function toIsoUtc(value: string | number | Date | null | undefined): string {
  if (value === null || value === undefined) {
    return '';
  }

  const date = toInstant(value);

  return date ? date.toISOString() : '';
}
